/**
 * 자동 페이지네이션 — ProseMirror Plugin
 *
 *  본문(paragraph 등 flowing 자식) 이 페이지 끝(여백 위) 을 넘어가면
 *  overflow 컨텐츠를 잘라 다음 페이지로 흘려보낸다 (Word/PDF 와 동일 UX).
 *
 *  설계 선택:
 *  - useEffect + editor.on("update") 대신 PM Plugin 으로 구현 — view.update lifecycle
 *    훅이 더 안정적이고 transaction 디스패치 타이밍 통제가 쉬움.
 *  - rAF 디바운스 — 한 번의 입력으로 measure 한 번만 수행.
 *  - 측정은 DOM 구조(querySelector + getBoundingClientRect) 기반 — PM widget/gap-cursor
 *    영향 차단.
 *  - 분할 시 사용자 selection 이 overflow 영역에 있었다면 옮겨진 위치로 따라가게 setSelection.
 *  - 분할/병합 후 contractPage.attrs.pageNumber 자동 재정렬.
 *
 *  알려진 한계 (v1):
 *  - 단일 paragraph 가 페이지보다 길면 split 보류 (mid-paragraph break 미지원).
 *    실사용에서는 Enter 로 단락 구분되니 거의 발생 X.
 *  - 본문 삭제로 페이지가 비어도 자동 병합/제거하지 않음.
 */
import {
  Plugin,
  PluginKey,
  TextSelection,
  Transaction,
} from "@tiptap/pm/state";
import { EditorView } from "@tiptap/pm/view";
import { A4, PAGINATION_USABLE_BOTTOM } from "./coords";

export const paginationPluginKey = new PluginKey("contract-auto-pagination");

/** Hysteresis — split / merge 간 sub-pixel flip-flop 방지.
 *  - Split: overflow 가 OVERFLOW_TOL 이상일 때만 발화 (sub-pixel reflow 차이 흡수)
 *  - Merge: 끌어올리려는 자식이 들어가고도 MERGE_SAFETY 만큼 여유가 남아야 발화
 *  두 임계값 사이의 deadband 가 있어야 layout reflow 시 재계산 차이로 인한 떨림이 사라진다.
 *  표(TABLE) 는 행/셀 단위로 layout 재계산 시 높이가 ±5~10px 오차 발생 가능 — MERGE_SAFETY 를 충분히 키워 안전 마진 확보. */
// OVERFLOW_TOL — 추출 시점 페이지 분할이 정확히 973 까지 채움 → DOM 렌더링 차이로 5-20px 정도
//   넘칠 수 있음. 작은 overflow 무시해서 사후 split 이 안 일어나도록 30 으로 설정 (HWP 와 동일 페이지수 유지).
const OVERFLOW_TOL = 30;
const MERGE_SAFETY = 32;
/** 직전 split / mid-split 직후엔 짧은 시간(ms) 동안 merge-up 보류 — 같은 컨텐츠 왕복 방지 */
const POST_SPLIT_MERGE_COOLDOWN_MS = 250;
/** 한 rAF 내 plugin 자체가 연속 dispatch 한 횟수 — 너무 많으면 오실레이션 의심, 강제 중단 */
const MAX_CONSECUTIVE_DISPATCHES = 16;

/** 한 element 의 PM 도큐먼트 outer 위치를 안전하게 역산 (exception 시 null) */
const outerPosOfDom = (view: EditorView, el: HTMLElement): number | null => {
  try {
    return view.posAtDOM(el, 0) - 1;
  } catch {
    return null;
  }
};

/** 단락 DOM 안에서 첫 번째로 페이지 경계(usableBottom 명목 px) 를 넘는 글자의
 *  PM 도큐먼트 위치를 찾는다. 줌 비율을 고려해 client rect 를 명목 px 로 환산.
 *  찾지 못하면 null. */
const findMidParagraphSplitPos = (
  view: EditorView,
  paragraphDom: HTMLElement,
  pageDom: HTMLElement,
  usableBottomNominal: number,
): number | null => {
  const pageRect = pageDom.getBoundingClientRect();
  const scale = pageRect.width > 0 ? pageRect.width / 794 : 1;
  const usableBottomClient = pageRect.top + usableBottomNominal * scale;

  const range = document.createRange();
  const walker = document.createTreeWalker(paragraphDom, NodeFilter.SHOW_TEXT);
  let found: { node: Text; offset: number } | null = null;
  let prevLineOffset: { node: Text; offset: number } | null = null;
  let prevLineBottom = -Infinity;

  while (walker.nextNode()) {
    const tn = walker.currentNode as Text;
    const len = tn.length;
    for (let i = 0; i < len; i++) {
      range.setStart(tn, i);
      range.setEnd(tn, i + 1);
      const r = range.getBoundingClientRect();
      if (r.height === 0) continue; // 공백·줄바꿈 등
      // 새 줄로 넘어갔으면 직전 줄 시작 글자를 후보로 기록
      if (r.bottom !== prevLineBottom) {
        prevLineOffset = { node: tn, offset: i };
        prevLineBottom = r.bottom;
      }
      if (r.bottom > usableBottomClient) {
        // 가능하면 줄 시작 위치로 split — 한 줄을 통째로 다음 페이지로 보내 더 깔끔
        found = prevLineOffset || { node: tn, offset: i };
        break;
      }
    }
    if (found) break;
  }
  if (!found) return null;
  try {
    const pos = view.posAtDOM(found.node, found.offset);
    return pos;
  } catch {
    return null;
  }
};

/** 표가 페이지 경계를 가로지르면 마지막으로 fit 되는 행 직후에서 split.
 *  ProseMirror tr.split(pos) 사용 — 표 노드를 안전하게 두 개로 나눔 (attrs 자동 보존).
 *  분할 불가 (단일 행도 페이지 보다 큼) 면 null. */
const trySplitTableAtPageBoundary = (
  view: EditorView,
  tableDom: HTMLTableElement,
  pageDom: HTMLElement,
  usableBottomNominal: number,
): Transaction | null => {
  // page rect 와 줌 비율 → client 좌표 기준 사용 가능 bottom
  const pageRect = pageDom.getBoundingClientRect();
  const scale = pageRect.width > 0 ? pageRect.width / 794 : 1;
  const usableBottomClient = pageRect.top + usableBottomNominal * scale;

  // table 의 직접 자식 tr 만 (중첩 테이블 무시)
  const tbody = tableDom.querySelector(":scope > tbody");
  const trs = tbody
    ? (Array.from(tbody.children).filter(
        (c) => c.tagName === "TR",
      ) as HTMLTableRowElement[])
    : (Array.from(
        tableDom.querySelectorAll(
          ":scope > tr, :scope > thead > tr, :scope > tbody > tr",
        ),
      ) as HTMLTableRowElement[]);
  if (trs.length < 2) {
    return null;
  }

  // 마지막으로 페이지 안에 fit 되는 row 인덱스 찾기 (tolerance 4px)
  let lastFitIdx = -1;
  for (let i = 0; i < trs.length; i++) {
    const r = trs[i].getBoundingClientRect();
    if (r.bottom <= usableBottomClient + 4) {
      lastFitIdx = i;
    } else {
      break;
    }
  }
  if (lastFitIdx < 0) return null;
  if (lastFitIdx >= trs.length - 1) return null;

  // PM doc 에서 table 위치 찾기
  let tablePos: number;
  try {
    tablePos = view.posAtDOM(tableDom, 0) - 1;
  } catch {
    return null;
  }
  if (tablePos < 0) return null;

  const state = view.state;
  const tableNode = state.doc.nodeAt(tablePos);
  if (!tableNode || tableNode.type.name !== "table") return null;

  // 분할 위치: lastFitIdx 행 끝 = (lastFitIdx+1)번째 행 시작
  //   tablePos = table 의 outer pos
  //   tablePos + 1 = table 안쪽 시작 (첫 행의 outer pos)
  //   여기에 row[0..lastFitIdx] 의 nodeSize 합을 더하면 = 분할 지점
  let offset = tablePos + 1; // table 안쪽 시작
  let rowIdx = 0;
  let splitPos = -1;
  tableNode.forEach((row, _, idx) => {
    if (idx === lastFitIdx + 1) splitPos = offset;
    offset += row.nodeSize;
    rowIdx = idx;
  });
  if (splitPos < 0) return null;
  if (rowIdx < lastFitIdx + 1) return null; // 안전 체크

  // tr.split(pos, depth) — depth=1 이면 가장 가까운 부모(=table) 만 분할.
  //   결과: [tableA(rows 0..lastFitIdx), tableB(rows lastFitIdx+1..)]
  try {
    const tr = state.tr.split(splitPos, 1);
    return tr;
  } catch {
    return null;
  }
};

/** flowing 자식 필터 — TipTap 이 table 을 div.tableWrapper 로 감싸므로 그것도 인식해야 함.
 *  반환은 항상 측정용 element (대부분 P/H/TABLE/DIV.tableWrapper). */
const isFlowingChild = (el: HTMLElement): boolean => {
  const tag = el.tagName;
  if (/^(P|H1|H2|H3|TABLE)$/.test(tag)) return true;
  if (tag === "DIV" && el.classList.contains("tableWrapper")) return true;
  return false;
};

/** 주어진 flowing 자식이 표(또는 표 wrapper)인지 — 표 처리 분기에 사용 */
const isTableFlowing = (el: HTMLElement): boolean => {
  if (el.tagName === "TABLE") return true;
  if (el.tagName === "DIV" && el.classList.contains("tableWrapper"))
    return true;
  return false;
};

/** flowing 자식에서 실제 <table> 요소 추출 (DIV.tableWrapper 인 경우 안의 table 반환) */
const getTableFromFlowing = (el: HTMLElement): HTMLTableElement | null => {
  if (el.tagName === "TABLE") return el as HTMLTableElement;
  if (el.tagName === "DIV" && el.classList.contains("tableWrapper")) {
    return el.querySelector(":scope > table") as HTMLTableElement | null;
  }
  return null;
};

/** 자동 페이지네이션 플러그인 */
export const buildPaginationPlugin = () =>
  new Plugin({
    key: paginationPluginKey,
    view(view) {
      let rafId = 0;
      let consecutivePluginDispatches = 0;
      let suspendUntil = 0;
      let lastSplitAt = 0;

      /** 플러그인이 자체 dispatch — 카운터 증가 + 임계값 도달 시 일시 정지 */
      const pluginDispatch = (tr: any) => {
        consecutivePluginDispatches += 1;
        if (consecutivePluginDispatches >= MAX_CONSECUTIVE_DISPATCHES) {
          suspendUntil = Date.now() + 800;
          consecutivePluginDispatches = 0;
          return;
        }
        view.dispatch(tr);
      };

      const paginate = () => {
        rafId = 0;
        if (Date.now() < suspendUntil) return;
        const { state } = view;
        const pageType = state.schema.nodes.contractPage;
        if (!pageType) return;

        const pageDoms = Array.from(
          view.dom.querySelectorAll("[data-contract-page]"),
        ) as HTMLElement[];
        if (!pageDoms.length) return;

        // ─── 1) Split — 첫 번째 overflow 페이지 탐지 ───────────────────────
        for (let i = 0; i < pageDoms.length; i++) {
          const pageDom = pageDoms[i];
          // P 태그만 flowing 자식으로 취급 (PM widget / gap-cursor 무시)
          const flowingChildren = (
            Array.from(pageDom.children) as HTMLElement[]
          ).filter(isFlowingChild);
          if (!flowingChildren.length) continue;

          const overflowChild = flowingChildren.find(
            (el) =>
              el.offsetTop + el.offsetHeight >
              PAGINATION_USABLE_BOTTOM + OVERFLOW_TOL,
          );
          if (!overflowChild) continue;

          // 단락이 페이지 경계를 가로지르는 경우 (단일 문단 또는 첫 자식 overflow):
          // mid-paragraph split — 경계 직전 줄에서 단락을 둘로 쪼개고 dispatch.
          // 다음 사이클에서 두 번째 단락이 정상 split 로직으로 다음 페이지에 push 된다.
          const startsAboveBound =
            overflowChild.offsetTop < PAGINATION_USABLE_BOTTOM;

          // ── 표 우선 처리: 행 분할 → 그래도 안 되면 다음 페이지로 통째 이동 ──
          //  사용자 의도: "표가 현재 페이지에 다 못 들어가면 다음 페이지로 내려서 그 페이지를 다 활용"
          //  즉, 표가 첫 자식이고 분할 불가일 때만 절대 못 옮기지만, 그 외엔 항상 보내야 함.
          if (isTableFlowing(overflowChild) && startsAboveBound) {
            const tableEl = getTableFromFlowing(overflowChild);
            if (!tableEl) continue;
            // 1) 우선 행 단위 분할 시도 — 일부 행이라도 fit 되면 거기서 split, 나머지 다음 페이지로
            const splitTr = trySplitTableAtPageBoundary(
              view,
              tableEl,
              pageDom,
              PAGINATION_USABLE_BOTTOM,
            );
            if (splitTr) {
              splitTr.setMeta(paginationPluginKey, {
                source: "table-row-split",
              });
              splitTr.setMeta("addToHistory", false);
              lastSplitAt = Date.now();
              pluginDispatch(splitTr);
              return;
            }
            // 2) 분할 불가 (한 행도 fit 안 됨) + 표가 첫 자식이 아님 → boundary split 으로 통째 이동 (아래에서 처리)
            // 3) 분할 불가 + 표가 첫/유일 자식 → 페이지 비울 수 없음, 보류 (overflow:hidden 으로 클리핑)
            if (
              overflowChild === flowingChildren[0] ||
              flowingChildren.length === 1
            ) {
              continue;
            }
            // fall through to boundary split (case 2)
          } else if (
            startsAboveBound &&
            (overflowChild === flowingChildren[0] ||
              flowingChildren.length === 1)
          ) {
            // 단일 단락이 페이지보다 큰 경우 mid-split
            const midPos = findMidParagraphSplitPos(
              view,
              overflowChild,
              pageDom,
              PAGINATION_USABLE_BOTTOM,
            );
            if (midPos != null) {
              const tr = state.tr.split(midPos);
              tr.setMeta(paginationPluginKey, { source: "mid-split" });
              tr.setMeta("addToHistory", false);
              lastSplitAt = Date.now();
              pluginDispatch(tr);
              return;
            }
            continue;
          }
          if (overflowChild === flowingChildren[0]) continue;

          const childOuterPos = outerPosOfDom(view, overflowChild);
          const pageOuterPos = outerPosOfDom(view, pageDom);
          if (childOuterPos == null || pageOuterPos == null) continue;
          const pageNode = state.doc.nodeAt(pageOuterPos);
          if (!pageNode || pageNode.type.name !== "contractPage") continue;

          const splitFrom = childOuterPos;
          const splitTo = pageOuterPos + 1 + pageNode.content.size;
          if (splitTo <= splitFrom) continue;

          const overflowSlice = state.doc.slice(splitFrom, splitTo).content;

          // 사용자 selection 이 overflow 안이었다면 옮겨진 위치로 따라가도록
          const selFrom = state.selection.from;
          const cursorInOverflow = selFrom >= splitFrom && selFrom <= splitTo;
          const cursorOffsetInSlice = cursorInOverflow
            ? selFrom - splitFrom
            : 0;

          const tr = state.tr.delete(splitFrom, splitTo);
          let insertedAt = -1;

          // ── 삽입 위치는 post-delete tr.doc 에서 직접 조회 (수식 기반 fragile 회피) ──
          //  pageOuterPos 는 delete 가 그 안쪽에서 일어났으므로 이동하지 않음.
          const truncatedPage = tr.doc.nodeAt(pageOuterPos);
          if (!truncatedPage || truncatedPage.type.name !== "contractPage")
            continue;
          const currentPageEnd = pageOuterPos + truncatedPage.nodeSize;

          const nextPageDom = pageDoms[i + 1];
          if (nextPageDom) {
            const nextOuter = outerPosOfDom(view, nextPageDom);
            if (nextOuter == null) continue;
            const mapped = tr.mapping.map(nextOuter);
            insertedAt = mapped + 1; // 다음 페이지 컨텐츠 시작
            tr.insert(insertedAt, overflowSlice);
          } else {
            // 새 contractPage 를 현재 페이지 직후에 삽입.
            // pageNumber 는 임시값(pageDoms.length+1) — 다음 cycle 의 renumber 가 정확히 재정렬.
            const newPage = pageType.create(
              {
                pageNumber: pageDoms.length + 1,
                width: A4.WIDTH_PX,
                height: A4.HEIGHT_PX,
              },
              overflowSlice,
            );
            insertedAt = currentPageEnd + 1; // 새 페이지 컨텐츠 시작
            tr.insert(currentPageEnd, newPage);
          }

          if (cursorInOverflow && insertedAt > 0) {
            const targetPos = Math.min(
              insertedAt + cursorOffsetInSlice,
              tr.doc.content.size,
            );
            try {
              tr.setSelection(TextSelection.near(tr.doc.resolve(targetPos)));
              tr.scrollIntoView();
            } catch {
              /* selection resolve 실패 시 무시 */
            }
          }

          tr.setMeta(paginationPluginKey, { source: "split" });
          tr.setMeta("addToHistory", false);
          lastSplitAt = Date.now();
          pluginDispatch(tr);
          return; // 추가 overflow 는 다음 view.update 에서 자연 처리
        }

        // ─── 2) Merge — 텍스트 삭제로 비어진 페이지 정리 + 다음 페이지 컨텐츠 끌어올리기 ──
        //  Word/PDF 처럼 본문이 줄어들면 페이지가 자동으로 줄어들도록.
        //  positionedText/절대좌표 필드(.contract-field-block) 가 있는 페이지는 임포트/배치 데이터로
        //  취급하여 자동 병합 대상에서 제외 — 사용자가 수동으로 다룬다.
        //  ⚠ inline 필드(.contract-field-inline)는 텍스트 흐름의 일부이므로 자동병합 대상 유지
        //     (단일레이어: 필드도 변수처럼 흐름 안에 있으므로 여기서 제외하면 안 됨).
        //  ⚠ 직전 split 직후 짧은 시간엔 merge-up 보류 — 갓 잘린 컨텐츠를 다시 끌어올려 split↔merge 왕복 방지.
        const isAutoFlowPage = (el: HTMLElement) =>
          !el.querySelector("[data-positioned-text], .contract-field-block");
        const inMergeCooldown =
          Date.now() - lastSplitAt < POST_SPLIT_MERGE_COOLDOWN_MS;

        for (let i = 0; i < pageDoms.length - 1; i++) {
          const pageDom = pageDoms[i];
          const nextPageDom = pageDoms[i + 1];
          if (!isAutoFlowPage(pageDom) || !isAutoFlowPage(nextPageDom))
            continue;

          const currentFlowing = (
            Array.from(pageDom.children) as HTMLElement[]
          ).filter(isFlowingChild);
          const nextFlowing = (
            Array.from(nextPageDom.children) as HTMLElement[]
          ).filter(isFlowingChild);

          // 다음 페이지가 완전히 비어 있으면(텍스트 없는 paragraph 만) 제거.
          // 단, 사용자 cursor 가 그 페이지 안에 있으면 보류 (입력 직전일 수 있음).
          const nextAllEmpty = nextFlowing.every(
            (el) => !(el.textContent || "").trim(),
          );
          if (nextAllEmpty) {
            const nextOuter = outerPosOfDom(view, nextPageDom);
            if (nextOuter == null) continue;
            const nextPageNode = state.doc.nodeAt(nextOuter);
            if (!nextPageNode) continue;
            const cursorInNext =
              state.selection.from > nextOuter &&
              state.selection.from < nextOuter + nextPageNode.nodeSize;
            if (cursorInNext) continue;
            const tr = state.tr.delete(
              nextOuter,
              nextOuter + nextPageNode.nodeSize,
            );
            tr.setMeta(paginationPluginKey, { source: "remove-empty-page" });
            tr.setMeta("addToHistory", false);
            pluginDispatch(tr);
            return;
          }

          // 현재 페이지에 남은 공간 계산
          const lastChild = currentFlowing[currentFlowing.length - 1];
          const lastBottom = lastChild
            ? lastChild.offsetTop + lastChild.offsetHeight
            : 0;
          const room = PAGINATION_USABLE_BOTTOM - lastBottom;
          if (room <= 0) continue;

          // 직전 split 직후 cooldown 내라면 merge-up 보류 — 같은 컨텐츠 왕복 방지
          if (inMergeCooldown) continue;
          // 다음 페이지의 첫 flowing 자식이 들어갈 수 있는지 확인.
          // MERGE_SAFETY 만큼 여유까지 확보된 경우에만 끌어올림 — 경계선에서 split 과 oscillate 방지
          const firstNext = nextFlowing[0];
          if (!firstNext) continue;
          // 표는 layout 재계산마다 ±5~10px 높이 오차 → 표를 끌어올리는 merge 는 보수적으로 생략
          if (isTableFlowing(firstNext)) continue;
          const moveHeight = firstNext.offsetHeight;
          if (moveHeight + MERGE_SAFETY > room) continue; // deadband — 안 들어가거나 빠듯하면 보류

          const firstNextOuter = outerPosOfDom(view, firstNext);
          const currentPageOuter = outerPosOfDom(view, pageDom);
          if (firstNextOuter == null || currentPageOuter == null) continue;
          const movingNode = state.doc.nodeAt(firstNextOuter);
          const currentPageNode = state.doc.nodeAt(currentPageOuter);
          if (!movingNode || !currentPageNode) continue;
          const currentPageEnd =
            currentPageOuter + 1 + currentPageNode.content.size;

          // 사용자 selection 이 이동 대상 안이었다면 새 위치로 따라가도록
          const selFrom = state.selection.from;
          const cursorInMoving =
            selFrom >= firstNextOuter &&
            selFrom <= firstNextOuter + movingNode.nodeSize;
          const cursorOffsetInNode = cursorInMoving
            ? selFrom - firstNextOuter
            : 0;

          const tr = state.tr.delete(
            firstNextOuter,
            firstNextOuter + movingNode.nodeSize,
          );
          // currentPageEnd 는 deletion 보다 앞이므로 mapping 영향 없음
          tr.insert(currentPageEnd, movingNode);

          if (cursorInMoving) {
            const targetPos = Math.min(
              currentPageEnd + cursorOffsetInNode,
              tr.doc.content.size,
            );
            try {
              tr.setSelection(TextSelection.near(tr.doc.resolve(targetPos)));
            } catch {
              /* ignore */
            }
          }

          tr.setMeta(paginationPluginKey, { source: "merge-up" });
          tr.setMeta("addToHistory", false);
          pluginDispatch(tr);
          return; // 다음 사이클에서 추가 병합/empty 페이지 제거 이어짐
        }

        let needsRenumber = false;
        const pageRefs: { pos: number; attrs: any }[] = [];
        state.doc.forEach((n, off) => {
          if (n.type.name === "contractPage") {
            pageRefs.push({ pos: off, attrs: n.attrs });
            if ((n.attrs.pageNumber ?? -1) !== pageRefs.length) {
              needsRenumber = true;
            }
          }
        });
        if (needsRenumber && pageRefs.length) {
          const tr = state.tr;
          pageRefs.forEach((p, idx) => {
            tr.setNodeMarkup(p.pos, undefined, {
              ...p.attrs,
              pageNumber: idx + 1,
            });
          });
          tr.setMeta("addToHistory", false);
          tr.setMeta(paginationPluginKey, { source: "renumber" });
          pluginDispatch(tr);
          return;
        }
        // 여기까지 도달했으면 split / merge / renumber 모두 발화 안 함 = 안정 상태.
        // 카운터 리셋 — 다음 사용자 입력으로 시작하는 새 사이클부터 다시 카운트.
        consecutivePluginDispatches = 0;
      };

      const schedule = () => {
        if (rafId) return;
        rafId = requestAnimationFrame(paginate);
      };

      // 초기 1회 + 이후 view.update 마다 (state 가 새로 들어왔을 때만).
      //  ⚠ 최초 schedule 은 레이아웃(폰트·표 높이)이 확정되기 전 측정될 수 있어,
      //     저장된 sparse 페이지(표 뒤 빈 공간 등)를 초기 로드에서 컴팩트하지 못하는 문제가 있었다.
      //     → 레이아웃 안정 후 여러 번 재스케줄해 merge/compact 캐스케이드를 확실히 킥오프한다.
      //     (각 재실행은 안정 상태면 아무것도 dispatch 하지 않으므로 무해)
      schedule();
      const initialKicks = [120, 350, 700, 1200].map((ms) =>
        setTimeout(schedule, ms),
      );

      // 숨김(display:none, 크기 0)으로 마운트된 에디터가 탭 전환으로 "표시"될 때
      //  ResizeObserver 가 0→실측 전환을 감지해 pagination 을 재실행 → 그 순간에 컴팩트.
      //  (검토/필드 → 본문 전환 시 본문 에디터가 뒤늦게 보이는 케이스)
      let prevW = 0;
      let ro: ResizeObserver | null = null;
      try {
        ro = new ResizeObserver(() => {
          const w = (view.dom as HTMLElement).offsetWidth;
          if (w > 0 && prevW === 0) schedule(); // 0 → 보임 전환에서만 킥
          prevW = w;
        });
        ro.observe(view.dom as HTMLElement);
      } catch {
        /* ResizeObserver 미지원 환경 무시 */
      }
      return {
        update(_view, prevState) {
          if (prevState.doc === _view.state.doc) return;
          // 마지막 doc 변경의 source 가 plugin 자체였는지 식별 — plugin 이 만든 tr 의 meta 는 _view.state.tr 의 마지막 transaction 으로 알 수 없으므로, lastTransaction 기반 추정.
          // 실용적 우회: lastUserDocChangeAt 을 외부 변경(=plugin 외) 에서만 갱신.
          schedule();
        },
        destroy() {
          if (rafId) cancelAnimationFrame(rafId);
          initialKicks.forEach((t) => clearTimeout(t));
        },
      };
    },
  });
